import {
  curry,
  isEmpty,
  isNil,
  lensPath,
  lensProp,
  mergeRight,
  pipe,
  propEq,
  set,
  view,
  find,
} from "ramda";
import {
  validateBasicDetails,
  validateBasicDetailsField,
} from "./basicDetailsValidation";
import * as basicDetailsActions from "./basicDetailsActions";
import * as submissionActions from "submission/submissionActions";
import * as introActions from "intro/introActions";
import { getDefaultLibraryId } from "application/applicationReducer";
import {
  completeStep,
  getErrors,
  getField,
  initialStepState,
  setCompleted,
  setErrors,
  setField,
  setFields,
  setLoadingLocalStorage,
} from "common/stepReducerCommon";

import {
  validateContributor,
  validateContributorField,
} from "file-details/fileDetailsValidation";
import { isSubmitterCreator } from "../intro/introReducer";
import * as steps from "common/steps";
import * as applicationReducer from "application/applicationReducer";

const initialPersonalInfoStepState = {
  countries: [],
  libraries: [],
  subdivisions: [],
  showLocalLibraryOther: false,
  fields: {
    country: {},
    region: {},
    localLibrary: {},
    role: {},
    contributor: {
      role: {},
      type: "PERSON",
    },
  },
};

const initialState = mergeRight(initialStepState, initialPersonalInfoStepState);

const countriesLens = lensProp("countries");
const setCountries = set(countriesLens);
export const getCountries = view(countriesLens);

const librariesLens = lensProp("libraries");
const setLibraries = set(librariesLens);
export const getLibraries = view(librariesLens);

const showLocalLibraryOtherLens = lensProp("showLocalLibraryOther");
const setShowLocalLibraryOther = set(showLocalLibraryOtherLens);
export const getShowLocalLibraryOther = view(showLocalLibraryOtherLens);

const subdivisionsLens = lensProp("subdivisions");
const setSubdivisions = set(subdivisionsLens);
export const getSubdivisions = view(subdivisionsLens);

const countryChanged = (country, state) => {
  return pipe(
    setField(["country"], country),
    setField(["region"], {}),
    setSubdivisions(initialState.subdivisions),
    setCompleted(false)
  )(state);
};

const regionChanged = (region, state) => {
  return pipe(setField(["region"], region), setCompleted(false))(state);
};

const roleChanged = (role, state) => {
  return pipe(setField(["role"], role), setCompleted(false))(state);
};

const localLibraryChanged = curry((library, state) => {
  return setField(["localLibrary"], library || {}, state);
});

const fieldOptions = (appState) => applicationReducer.getFieldOptions(appState);

const fieldBlurred = (field, value, fieldOptions, state) =>
  setErrors(
    validateBasicDetailsField(field, value, fieldOptions, getErrors(state)),
    state
  );

const contributorFieldBlurred = (field, value, fieldOptions, state) =>
  setErrors(
    validateContributorField(field, value, fieldOptions, getErrors(state)),
    state
  );

const fieldChanged = (path, value, state) =>
  pipe(setField(path, value), setCompleted(false))(state);

const localStorageLoaded = (data, state) =>
  pipe(
    setLoadingLocalStorage(false),
    setFields(isEmpty(data) ? initialState.fields : data.fields),
    setCompleted(isEmpty(data) ? initialState.completed : data.completed)
  )(state);

const cancelSubmission = pipe(
  setCompleted(false),
  setFields(initialState.fields),
  setErrors(initialState.errors)
);

const updateDefaultLocalLibrary = curry((defaultLocalLibraryId, state) => {
  const localLibrary = getField(["localLibrary"], state);
  const libraries = getLibraries(state);
  const defaultLibrary = find(
    propEq("organizationId", defaultLocalLibraryId),
    libraries
  );

  return isEmpty(localLibrary) &&
    !isNil(defaultLocalLibraryId) &&
    !isEmpty(defaultLocalLibraryId)
    ? localLibraryChanged(defaultLibrary, state)
    : state;
});

const contributorRoleChanged = (role, state) => {
  return pipe(
    setField(["contributor", "role"], role),
    setCompleted(false)
  )(state);
};

export const contributorFieldsLens = lensPath(["fields", "contributor"]);
export const setContributorFields = set(contributorFieldsLens);
export const getContributorFields = view(contributorFieldsLens);
export const setContributorField = curry((path, value, state) =>
  set(lensPath(["fields", "contributor", ...path]), value, state)
);

export const contributorErrorsLens = lensPath(["errors"]);
export const setContributorErrors = set(contributorErrorsLens);
export const getContributorErrors = view(contributorErrorsLens);

const validateContributorForm = (fieldOptions) =>
  pipe(view(lensPath(["contributor"])), validateContributor(fieldOptions));

const submitterIsCreatorChanged = (applicationState, state) => {
  return pipe(
    setFields(initialState.fields),
    updateDefaultLocalLibrary(getDefaultLibraryId(applicationState)),
    setCompleted(false)
  )(state);
};

const addAnotherWork = pipe(
  setCompleted(false),
  setErrors(initialState.errors)
);

const basicDetailsReducer = (
  state = initialState,
  action,
  submissionState,
  applicationState,
  introState
) => {
  switch (action.type) {
    case basicDetailsActions.COMPLETE_STEP:
    case submissionActions.SUBMIT: {
      if (isSubmitterCreator(introState)) {
        console.log("Validating Basic Details");
        return completeStep(
          validateBasicDetails(fieldOptions(applicationState)),
          state
        );
      } else {
        console.log("Validating Contributor");
        return completeStep(
          validateContributorForm(fieldOptions(applicationState)),
          state
        );
      }
    }

    case basicDetailsActions.COUNTRIES_LOADED:
      return setCountries(action.countries, state);

    case basicDetailsActions.COUNTRY_CHANGED:
    case introActions.COUNTRY_CHANGED:
      return countryChanged(action.country, state);

    case basicDetailsActions.FIELD_BLURRED:
      return fieldBlurred(
        action.field,
        action.value,
        fieldOptions(applicationState),
        state
      );

    case basicDetailsActions.FIELD_CHANGED:
      return fieldChanged(action.path, action.value, state);

    case basicDetailsActions.LIBRARIES_LOADED:
      return pipe(
        setLibraries(action.libraries),
        updateDefaultLocalLibrary(getDefaultLibraryId(applicationState))
      )(state);

    case basicDetailsActions.LOCAL_LIBRARY_CHANGED:
      return pipe(
        localLibraryChanged(action.library),
        setCompleted(false)
      )(state);

    case basicDetailsActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case basicDetailsActions.REGION_CHANGED:
      return regionChanged(action.region, state);

    case basicDetailsActions.ROLE_CHANGED:
      return roleChanged(action.role, state);

    case basicDetailsActions.SET_SHOW_LOCAL_LIBRARY_OTHER:
      return pipe(
        setField(["localLibraryOther"], ""),
        setField(["localLibrary"], {}),
        setShowLocalLibraryOther(action.show),
        setCompleted(false)
      )(state);

    case basicDetailsActions.SUBDIVISIONS_LOADED:
      return setSubdivisions(action.subdivisions, state);

    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    case basicDetailsActions.CONTRIBUTOR_FIELD_BLURRED:
      return contributorFieldBlurred(
        action.field,
        action.value,
        fieldOptions(applicationState),
        state
      );

    case basicDetailsActions.CONTRIBUTOR_FIELD_CHANGED:
      return setContributorField(action.path, action.value, state);

    case basicDetailsActions.CONTRIBUTOR_ROLE_CHANGED:
      return contributorRoleChanged(action.role, state);

    case introActions.SUBMITTER_IS_CONTRIBUTOR_CHANGED:
      return submitterIsCreatorChanged(applicationState, state);

    case submissionActions.ADD_ANOTHER_WORK:
      return addAnotherWork(state);

    case submissionActions.SET_ACTIVE_STEP:
      return action.step === steps.BASIC_DETAILS
        ? setCompleted(false)(state)
        : state;

    default:
      return state;
  }
};

export default basicDetailsReducer;
